var Tooltip_1;
import { __decorate } from "tslib";
import { customElement, isAncestor } from '@here/hds-base';
import { html, LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { property, query } from 'lit/decorators.js';
import { styles } from './hds-tooltip.css.js';
import { computePosition, offset } from '@floating-ui/dom';
/**
 *
 * @cssprop --hds-tooltip--z-index  z-index value, default is `1`
 * @cssprop --hds-tooltip--animation-delay  Animation delay, default is `0ms`
 * @cssprop --hds-tooltip--animation-duration Animation duration, default is `var(--hds-animation-duration-medium)`
 * @cssprop --hds-tooltip--animation-timing-function Animation timing-function, default is `var(--hds-animation-cubic-bezier-entry-default)`
 *
 * */
let Tooltip = Tooltip_1 = class Tooltip extends LitElement {
    constructor() {
        super();
        this.open = false;
        this.interactive = false;
        this.disabled = false;
        this.triggerBy = ['mouseenter', 'focus'];
        this.placement = 'top';
        this.onDocumentMouseMove = this.onDocumentMouseMove.bind(this);
        this.onViewChange = this.onViewChange.bind(this);
        this.onWindowFocus = this.onWindowFocus.bind(this);
        this.onWindowBlur = this.onWindowBlur.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
    }
    connectedCallback() {
        super.connectedCallback && super.connectedCallback();
        this.ownerDocument.addEventListener('mousemove', this.onDocumentMouseMove, true);
        this.ownerDocument.addEventListener('click', this.onDocumentClick, true);
        this.ownerDocument.defaultView?.addEventListener('focus', this.onWindowFocus, true);
        this.ownerDocument.defaultView?.addEventListener('blur', this.onWindowBlur, true);
        this.ownerDocument.defaultView?.addEventListener('scroll', this.onViewChange, true);
        this.ownerDocument.defaultView?.addEventListener('resize', this.onViewChange, true);
    }
    disconnectedCallback() {
        this.ownerDocument.removeEventListener('mousemove', this.onDocumentMouseMove, true);
        this.ownerDocument.removeEventListener('click', this.onDocumentClick, true);
        this.ownerDocument.defaultView?.removeEventListener('focus', this.onWindowFocus, true);
        this.ownerDocument.defaultView?.removeEventListener('blur', this.onWindowBlur, true);
        this.ownerDocument.defaultView?.removeEventListener('scroll', this.onViewChange, true);
        this.ownerDocument.defaultView?.removeEventListener('resize', this.onViewChange, true);
        super.disconnectedCallback && super.disconnectedCallback();
    }
    async firstUpdated() {
        if (this.id && !this.trigger) {
            this.trigger = `[data-hds-tooltip="${this.id}"]`;
        }
    }
    async updated(_) {
        this.updatePosition();
    }
    _openByTrigger(triggerElement) {
        const actualTrigger = triggerElement && triggerElement.closest && triggerElement.closest(this.trigger);
        if (actualTrigger) {
            this.open = true;
        }
    }
    _closeByTrigger(triggerElement) {
        const actualTrigger = triggerElement && triggerElement.closest && triggerElement.closest(this.trigger);
        if (actualTrigger) {
            this.open = false;
        }
    }
    onWindowFocus(e) {
        if (this.disabled)
            return;
        if (this.open)
            return;
        if (!this.triggerBy.includes('focus'))
            return;
        this._openByTrigger(e.target);
    }
    onWindowBlur(e) {
        if (!this.open)
            return;
        if (!this.triggerBy.includes('focus'))
            return;
        this._closeByTrigger(e.target);
    }
    onDocumentClick(e) {
        if (this.disabled)
            return;
        if (this.open)
            return;
        if (!this.triggerBy.includes('click'))
            return;
        this._openByTrigger(e.target);
    }
    onViewChange() {
        if (!this.disabled && this.open) {
            this.open = false;
        }
    }
    onDocumentMouseMove(e) {
        if (this.disabled)
            return;
        const trigger = e.target;
        const actualTrigger = trigger && trigger.closest && trigger.closest(this.trigger);
        if (this.open) {
            const isTargetTooltipElement = !!isAncestor(this, trigger);
            const isTargetTriggerElement = !!actualTrigger;
            const canCloseNonInteractive = !this.interactive && !isTargetTriggerElement;
            const canCloseInteractive = this.interactive && !(isTargetTooltipElement || isTargetTriggerElement);
            if (canCloseNonInteractive || canCloseInteractive) {
                this.open = false;
            }
        }
        else {
            const canOpenByOverflow = actualTrigger &&
                actualTrigger instanceof HTMLElement &&
                actualTrigger.scrollWidth > actualTrigger.offsetWidth;
            const canOpen = actualTrigger &&
                (this.triggerBy.includes('mouseenter') || this.triggerBy.includes('mouseenter-overflow')) &&
                (this.triggerBy.includes('mouseenter-overflow') ? canOpenByOverflow : true);
            if (canOpen) {
                this.open = true;
            }
        }
    }
    async updatePosition() {
        if (this.disabled)
            return;
        const actualTrigger = [...this.ownerDocument.querySelectorAll(this.trigger)].pop();
        if (actualTrigger) {
            const pos = await Tooltip_1.utils.computePosition(actualTrigger, this.tooltipWrapper, {
                strategy: 'fixed',
                placement: this.placement,
                middleware: [Tooltip_1.utils.offset(0)],
            });
            Object.assign(this.tooltipWrapper.style, {
                top: `${pos.y}px`,
                left: `${pos.x}px`,
            });
        }
    }
    render() {
        const cssClasses = {
            open: this.open,
            '-hds-tooltip-wrapper': true,
            [`placement-${this.placement}`]: true,
        };
        return html `<div class="${classMap(cssClasses)}">
      <div class="tooltip-arrow"></div>
      <div class="tooltip">
        <slot></slot>
      </div>
    </div>`;
    }
};
Tooltip.shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
Tooltip.styles = styles;
Tooltip.utils = { computePosition, offset };
__decorate([
    property({ type: Boolean, reflect: true })
], Tooltip.prototype, "open", void 0);
__decorate([
    property({ type: Boolean })
], Tooltip.prototype, "interactive", void 0);
__decorate([
    property({ type: Boolean })
], Tooltip.prototype, "disabled", void 0);
__decorate([
    property({
        type: String,
        attribute: 'trigger-by',
        converter: { fromAttribute: value => value?.split(' ') },
    })
], Tooltip.prototype, "triggerBy", void 0);
__decorate([
    property({ type: String, attribute: 'trigger' })
], Tooltip.prototype, "trigger", void 0);
__decorate([
    property({ type: String })
], Tooltip.prototype, "placement", void 0);
__decorate([
    query('.-hds-tooltip-wrapper')
], Tooltip.prototype, "tooltipWrapper", void 0);
Tooltip = Tooltip_1 = __decorate([
    customElement('hds-tooltip')
], Tooltip);
export { Tooltip };
//# sourceMappingURL=hds-tooltip.js.map